/**
 * Tiny inline trend chart for KPI cards — Grafana stat-panel style.
 *
 * Pure SVG, no recharts. recharts adds ~80kb of layout machinery for
 * an axis-less polyline, and KPI cards live in tight strips where 5
 * sparklines render simultaneously. SVG path math is ~30 lines and
 * the bundle stays slim.
 *
 * Visual contract:
 * - Stroke at the top (the actual line)
 * - Soft area-fill underneath fading to transparent
 * - Last-point dot for a "current value" anchor
 *
 * Long series (>120 points) get evenly downsampled before drawing —
 * a 365-day daily reading would otherwise produce a sub-pixel mash;
 * the eye reads ~60 points cleanly anyway.
 */

import { useId } from "react"

const MAX_POINTS = 120
const DEFAULT_WIDTH = 120
const DEFAULT_HEIGHT = 28
const STROKE_WIDTH = 1.5

export interface KpiSparklineProps {
  values: ReadonlyArray<number>
  /** CSS colour string (hex / hsl()). Default: currentColor. */
  color?: string
  width?: number
  height?: number
  className?: string
}

export function KpiSparkline({
  values,
  color,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  className,
}: KpiSparklineProps) {
  // Each gradient gets a unique id per render so two sparklines on
  // the same page don't end up referencing each other's defs.
  const gradientId = useId()
  const points = normalisePoints(values, width, height)
  if (points.length < 2) return null

  const linePath = toLinePath(points)
  const areaPath = toAreaPath(points, height)
  const last = points[points.length - 1]!
  const stroke = color ?? "currentColor"

  return (
    <svg
      aria-hidden="true"
      className={className}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
          <stop offset="100%" stopColor={stroke} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={STROKE_WIDTH}
      />
      <circle cx={last.x} cy={last.y} fill={stroke} r={1.8} />
    </svg>
  )
}

// ── pure helpers ────────────────────────────────────────────────────

interface Point {
  x: number
  y: number
}

/**
 * Map raw readings to viewport coordinates. Downsamples to MAX_POINTS
 * by stride sampling; a 1000-point input becomes 120 evenly spaced
 * points. Equal-value series (min === max) draw a flat line at the
 * vertical midpoint.
 */

export function normalisePoints(
  values: ReadonlyArray<number>,
  width: number,
  height: number
): Point[] {
  const finite = values.filter((v) => typeof v === "number" && Number.isFinite(v))
  if (finite.length < 2) return []

  // Downsample by stride to keep <= MAX_POINTS.
  const stride = finite.length <= MAX_POINTS ? 1 : Math.ceil(finite.length / MAX_POINTS)
  const sampled: number[] = []
  for (let i = 0; i < finite.length; i += stride) sampled.push(finite[i]!)
  // Always include the last reading so the "current value" dot sits
  // on the actual end-point even when stride > 1.
  if (sampled[sampled.length - 1] !== finite[finite.length - 1]) {
    sampled.push(finite[finite.length - 1]!)
  }

  let min = Infinity
  let max = -Infinity
  for (const v of sampled) {
    if (v < min) min = v
    if (v > max) max = v
  }
  const range = max - min
  const flat = range === 0
  // Padding (~10% top + bottom) so the line never touches the box edge.
  const PAD = height * 0.15
  const usable = height - PAD * 2

  const lastIndex = sampled.length - 1
  return sampled.map((v, i) => {
    const x = lastIndex === 0 ? width / 2 : (i / lastIndex) * width
    const y = flat ? height / 2 : PAD + (1 - (v - min) / range) * usable
    return { x, y }
  })
}

/** Build a "M x,y L x,y …" polyline. */

function toLinePath(points: ReadonlyArray<Point>): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ")
}

/**
 * Closed polygon for the area fill: the polyline + drop down to the
 * baseline + return to start. Baseline = bottom of the viewbox.
 */

function toAreaPath(points: ReadonlyArray<Point>, height: number): string {
  if (points.length === 0) return ""
  const line = toLinePath(points)
  const last = points[points.length - 1]!
  const first = points[0]!
  return `${line} L${last.x.toFixed(2)},${height} L${first.x.toFixed(2)},${height} Z`
}
